<!-- 批量发货：导出待发货清单 / 导入回写 -->
<template>
  <Dialog v-model="dialogVisible" title="批量发货导入" width="560px">
    <el-alert
      :closable="false"
      class="mb-12px"
      show-icon
      type="info"
      title="请先导出待发货清单，在「物流公司」「物流单号」列填写后上传。物流公司须与第 2 个 Sheet「可用物流公司」名称完全一致；填「无需发货」表示无需物流。"
    />
    <el-upload
      ref="uploadRef"
      v-model:file-list="fileList"
      :auto-upload="false"
      :disabled="formLoading"
      :limit="1"
      :on-exceed="handleExceed"
      accept=".xlsx, .xls"
      action="none"
      drag
    >
      <Icon icon="ep:upload" />
      <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      <template #tip>
        <div class="el-upload__tip text-center">
          <span>仅允许导入 xls、xlsx 格式文件。</span>
        </div>
      </template>
    </el-upload>

    <div v-if="result" class="mt-16px">
      <el-alert
        :closable="false"
        :title="`成功 ${result.successCount} 条，失败 ${result.failureCount} 条`"
        :type="result.failureCount ? 'warning' : 'success'"
        show-icon
      />
      <el-table
        v-if="result.failures?.length"
        :data="result.failures"
        class="mt-12px"
        max-height="280"
        size="small"
        stripe
      >
        <el-table-column label="行号" prop="rowIndex" width="70" />
        <el-table-column label="订单号" min-width="160" prop="orderNo" show-overflow-tooltip />
        <el-table-column label="失败原因" min-width="200" prop="reason" show-overflow-tooltip />
      </el-table>
    </div>

    <template #footer>
      <el-button @click="dialogVisible = false">关 闭</el-button>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">开始导入</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import type { UploadUserFile } from 'element-plus'

defineOptions({ name: 'OrderDeliveryImportForm' })

export interface DeliveryImportFailure {
  rowIndex?: number
  orderNo?: string
  reason?: string
}

export interface DeliveryImportResult {
  successCount: number
  failureCount: number
  failures?: DeliveryImportFailure[]
}

const props = defineProps<{
  /** 上传 FormData（含 file）并返回导入结果 */
  importApi: (formData: FormData) => Promise<DeliveryImportResult>
}>()

const message = useMessage()
const dialogVisible = ref(false)
const formLoading = ref(false)
const uploadRef = ref()
const fileList = ref<UploadUserFile[]>([])
const result = ref<DeliveryImportResult | null>(null)

const open = async () => {
  dialogVisible.value = true
  result.value = null
  fileList.value = []
  await nextTick()
  uploadRef.value?.clearFiles()
}
defineExpose({ open })

const emit = defineEmits<{ success: [] }>()

const submitForm = async () => {
  if (fileList.value.length === 0) {
    message.error('请上传发货 Excel 文件')
    return
  }
  const raw = fileList.value[0].raw
  if (!raw) {
    message.error('请重新选择文件')
    return
  }
  formLoading.value = true
  result.value = null
  try {
    const formData = new FormData()
    formData.append('file', raw)
    const data = await props.importApi(formData)
    result.value = data
    if (data.successCount > 0) {
      emit('success')
    }
    if (!data.failureCount) {
      message.success(`全部发货成功，共 ${data.successCount} 条`)
    } else {
      message.warning(`成功 ${data.successCount} 条，失败 ${data.failureCount} 条，请查看明细`)
    }
  } catch {
    message.error('导入失败，请检查文件后重试')
  } finally {
    formLoading.value = false
  }
}

const handleExceed = () => {
  message.error('最多只能上传一个文件')
}
</script>
